import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaPrint, FaRedoAlt, FaReceipt } from "react-icons/fa";
import { printThermal } from "../utils/exportHelpers";

const RetryPrintScreenPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const previewRef = useRef(null);

  const initialText = useMemo(() => {
    const state = location.state || {};
    return state.billText || state.text || "";
  }, [location.state]);

  const [text, setText] = useState(initialText);
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    if (!String(text || "").trim()) {
      toast.error("No bill content available for printing");
      return;
    }
    setLoading(true);
    try {
      await printThermal(previewRef.current, "Retry Print");
      toast.success("Print command sent");
    } catch (error) {
      toast.error(error?.message || "Unable to print");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Hero>
        <HeroLeft>
          <Badge><FaReceipt /> Retry Print</Badge>
          <h1>Receipt Recovery Console</h1>
          <p>Resend failed bills instantly to your thermal printer with live preview.</p>
        </HeroLeft>
        <ActionRow>
          <Btn type="button" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </Btn>
          <Btn type="button" $primary onClick={handlePrint} disabled={loading}>
            {loading ? <FaPrint /> : <FaRedoAlt />}
            {loading ? "Printing..." : "Retry Thermal Print"}
          </Btn>
        </ActionRow>
      </Hero>

      <Shell>
        <Card>
          <SectionHead>
            <h3>Bill Editor</h3>
            <span>{(text || "").length} chars</span>
          </SectionHead>
          <TextArea
            id="retry-print-content"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste or edit bill content"
          />
        </Card>

        <Card>
          <SectionHead>
            <h3>Thermal Preview</h3>
            <span>{(text || "").split("\n").length} lines</span>
          </SectionHead>
          <PreviewWrap>
            <Preview ref={previewRef}>
              {(text || "No content").split("\n").map((line, i) => (
                <div key={`${i}-${line}`}>{line || " "}</div>
              ))}
            </Preview>
          </PreviewWrap>
        </Card>
      </Shell>
    </Page>
  );
};

export default RetryPrintScreenPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 18px;
  background:
    radial-gradient(760px 380px at 5% -10%, rgba(1, 91, 154, 0.18), transparent 62%),
    radial-gradient(620px 320px at 100% 0%, rgba(14, 165, 233, 0.16), transparent 58%),
    linear-gradient(180deg, #f7fbff 0%, #f3f7fb 60%, #eef2f7 100%);
  display: grid;
  gap: 14px;
`;

const Hero = styled.div`
  background: linear-gradient(145deg, #024a7d 0%, #01345a 100%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 18px;
  padding: 18px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 14px;
  box-shadow: 0 20px 40px rgba(2, 38, 66, 0.24);
  h1 {
    margin: 8px 0 4px;
    font-size: 1.65rem;
    color: #fff;
    line-height: 1.15;
  }
  p {
    margin: 0;
    color: rgba(226, 232, 240, 0.95);
    max-width: 560px;
  }

  @media (max-width: 860px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeroLeft = styled.div`
  display: grid;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: fit-content;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(148, 210, 255, 0.16);
  color: #f8fafc;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Shell = styled.div`
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  gap: 14px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 16px;
  padding: 14px;
  display: grid;
  gap: 10px;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 1rem;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 999px;
    padding: 5px 10px;
  }
`;

const TextArea = styled.textarea`
  min-height: 460px;
  resize: vertical;
  border: 1px solid #d0d8e2;
  border-radius: 12px;
  padding: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
  }

  @media (max-width: 980px) {
    min-height: 240px;
  }
`;

const PreviewWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 4px 4px;
`;

const Preview = styled.div`
  border: 1px dashed #94a3b8;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
  width: min(360px, 100%);
  min-height: 460px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;

  @media (max-width: 980px) {
    min-height: 240px;
  }
`;

const Btn = styled.button`
  height: 42px;
  border-radius: 11px;
  padding: 0 14px;
  border: 1px solid ${({ $primary }) => ($primary ? "transparent" : "#cbd5e1")};
  background: ${({ $primary }) =>
    $primary ? "linear-gradient(135deg,#06b6d4,#0ea5e9)" : "#fff"};
  color: ${({ $primary }) => ($primary ? "#03253f" : "#0f172a")};
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: ${({ $primary }) =>
    $primary ? "0 10px 22px rgba(14,165,233,.35)" : "0 4px 12px rgba(2,6,23,.08)"};
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;
